/* eslint-disable no-nested-ternary */
import React, { Ref } from 'react'
import { MagnifyingGlassIcon, MapPinIcon } from '@heroicons/react/24/outline'
import Icon, { IconProps } from './icon'

export type InputProps = {
  append?: React.ReactNode
  autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters'
  autoComplete?:
  | 'off'
  | 'on'
  | 'name'
  | 'email'
  | 'username'
  | 'new-password'
  | 'current-password'
  | 'one-time-code'
  | 'given-name'
  | 'family-name'
  icon?: IconProps['icon']
  onChange?: (value: string) => void
  onClear?: () => void
  onFocus?: () => void
  onBlur?: () => void
  onKeyDown?: React.KeyboardEventHandler
  placeholder?: string
  prepend?: React.ReactNode
  spellCheck?: boolean
  disabled?: boolean
  type?:
  | 'text'
  | 'password'
  | 'email'
  | 'number'
  | 'tel'
  | 'url'
  | 'date'
  | 'checkbox'
  value?: string
  form?: boolean
  name?: string
  label?: string
  error?: string
  required?: boolean
  ariaDescribedBy?: string
}

const Input: React.FC<InputProps> = React.forwardRef(
  (
    {
      append,
      autoCapitalize = 'none',
      autoComplete = 'off',
      icon,
      onChange,
      onClear = () => undefined,
      onFocus,
      onBlur,
      onKeyDown,
      placeholder,
      prepend,
      spellCheck = false,
      disabled,
      type = 'text',
      value = '',
      form = false,
      name,
      label,
      error,
      required,
      ariaDescribedBy,
    },
    ref: Ref<HTMLInputElement>,
  ) => {
    const inputId = label ? `input-${label}` : undefined
    const errorId = error ? `${inputId}-error` : undefined
    const describedBy = [ariaDescribedBy, errorId].filter(Boolean).join(' ') || undefined

    let inputClassName =
      'grow border-0 outline-0 focus:ring-0 placeholder:text-gray-400 min-w-0 p-0'
    let className =
      'flex flex-row items-center w-full gap-2 px-3 bg-white border rounded-full focus-within:border-red h-9 border-night'
    let color = 'black'
    if (form) {
      inputClassName += ' placeholder:text-sm'
      className =
        'flex flex-row items-center w-full gap-2 p-3 px-3 bg-white border-2 border-gray-200 rounded-full focus-within:border-red h-9'
      color = 'gray'
    }

    if (error) {
      className += ' border-red-500 focus-within:border-red-500'
    }

    return (
      <div className="flex flex-col w-full">
        <label
          className={className}
          htmlFor={inputId}
          aria-invalid={error ? 'true' : undefined}
        >
          {label && <span className="sr-only">{label}</span>}
          {prepend}
          {icon && (
            <div aria-hidden="true">
              {icon === 'pin' ? (
                <MapPinIcon className="w-6 h-6 -mt-[0.5] text-night" />
              ) : icon === 'search' ? (
                <MagnifyingGlassIcon className="w-6 h-6 -mt-[0.5] text-night" />
              ) : (
                <Icon icon={icon} color={color} />
              )}
            </div>
          )}
          <input
            id={inputId}
            ref={ref}
            type={type}
            value={value}
            name={name}
            disabled={disabled}
            placeholder={placeholder}
            className={inputClassName}
            autoCapitalize={autoCapitalize}
            autoComplete={autoComplete}
            spellCheck={spellCheck}
            onFocus={onFocus}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            onChange={(event) => {
              if (onChange) onChange(event.target.value)
            }}
            aria-required={required}
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={describedBy}
            aria-disabled={disabled}
          />
          {value?.length !== 0 && (
            <button
              type="button"
              className="w-5 h-5"
              onClick={() => {
                if (onChange) onChange('')
                if (onClear) onClear()
              }}
              aria-label="Effacer le contenu"
            >
              <Icon icon="close" color="black" aria-hidden="true" />
            </button>
          )}
          {append}
        </label>
        {error && (
          <div
            id={errorId}
            role="alert"
            className="mt-1 text-sm text-red-500"
          >
            {error}
          </div>
        )}
      </div>
    )
  },
)

Input.displayName = 'Input'

export default Input
